import React from 'react';
import {
  Shield,
  Users,
  Building,
  CheckCircle,
  Award,
  TrendingUp,
  Heart,
  Clock,
  Target,
  Eye,
  ThumbsUp,
  FileCheck,
  HandCoins,
  UserCheck,
  Briefcase,
  Star,
  PhoneCall,
  Sparkles,
  BadgeCheck,
  Gem,
  Wallet,
  BarChart,
  MessageSquare,
  Headphones,
} from 'lucide-react';

const AboutSection = () => {
  const stats = [
    { icon: Clock, value: '10Y+', label: 'Industry Experience', suffix: '' },
    { icon: Users, value: '5K+', label: 'Happy Clients', suffix: '' },
    { icon: Building, value: '50+', label: 'Bank & NBFC Partners', suffix: '' },
    { icon: Shield, value: 'ISO', label: 'Certified & Secure', suffix: '' },
    { icon: Wallet, value: '₹500Cr+', label: 'Loans Disbursed', suffix: '' },
    { icon: Star, value: '98%', label: 'Customer Satisfaction', suffix: '' },
  ];
  const whyChooseUs = [
    {
      icon: Shield,
      title: 'Trusted & Secure',
      desc: 'ISO 9001:2015 certified with bank-level data encryption and RBI compliant processes.',
    },
    {
      icon: TrendingUp,
      title: 'Lowest Interest Rates',
      desc: 'We compare 50+ lenders to secure you the most competitive rates starting from 8.5% p.a.',
    },
    {
      icon: Clock,
      title: 'Instant Approvals',
      desc: 'Get in-principle approval within 60 minutes. Disbursal in as little as 24 hours.',
    },
    {
      icon: Heart,
      title: 'Tailored Solutions',
      desc: 'Customised loan products for salaried, self-employed, startups, and corporates.',
    },
    {
      icon: Award,
      title: 'Expert Advisory',
      desc: 'Free CIBIL score check and professional guidance from experienced financial analysts.',
    },
    {
      icon: CheckCircle,
      title: '100% Transparency',
      desc: 'Zero hidden charges – all fees and interest breakouts shared upfront.',
    },
    {
      icon: Headphones,
      title: 'Dedicated Support',
      desc: 'Personal relationship manager assigned to every client, 6 days a week.',
    },
    {
      icon: Gem,
      title: 'Flexible Tenures',
      desc: 'Loan tenures ranging from 12 to 84 months, with prepayment options.',
    },
  ];
  const processSteps = [
    {
      icon: FileCheck,
      title: 'Apply Online',
      desc: 'Fill our simple 2-minute application with basic KYC and income details.',
    },
    {
      icon: Users,
      title: 'Expert Consultation',
      desc: 'Our advisor reviews your profile and suggests the best loan options.',
    },
    {
      icon: HandCoins,
      title: 'Sanction & Disbursal',
      desc: 'Loan sanctioned within 24–48 hours, money credited directly to your account.',
    },
  ];
  const testimonials = [
    {
      name: 'Ramesh Gupta',
      role: 'Business Owner',
      content:
        'CashPro helped me secure a business loan of ₹50 lakhs at a very competitive rate. Their team handled everything from documentation to disbursal within 3 days. Highly professional!',
      rating: 5,
    },
    {
      name: 'Priya Sharma',
      role: 'Salaried Professional',
      content:
        'I was struggling with low CIBIL score, but CashPro arranged a personal loan that improved my credit health. Transparent process and no pressure selling. Thank you!',
      rating: 5,
    },
  ];

  return (
    <section className="py-12 md:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-20 animate-fade-in-up">
          <p className="text-green-600 font-semibold tracking-wide text-sm uppercase mb-2 flex items-center justify-center gap-2">
            <Sparkles size={16} />
            Since 2014 • India’s Trusted Financial Partner
            <Sparkles size={16} />
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800">
            Bridging Your Dreams{' '}
            <span className="text-green-600">With Smart Finance</span>
          </h1>
          <div className="w-24 h-1 bg-green-600 mx-auto mt-6 rounded-full" />
          <p className="text-gray-600 max-w-3xl mx-auto mt-6 text-lg">
            Personal loans • Business loans • Home loans • Loan against property • Education loans
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="space-y-6 animate-fade-in-up animation-delay-200">
            <p className="text-gray-600 text-lg leading-relaxed">
              At <strong className="text-green-700">CashPro Financial Services</strong>, we believe financial
              assistance should be as unique as your goals. Over the last decade, we’ve empowered
              <strong> 5,000+ families and businesses</strong> with timely, affordable credit solutions.
            </p>
            <p className="text-gray-600">
              Navigating financial growth in a fast-paced economy requires a partner who understands your
              needs. Whether it's a medical emergency, business expansion, wedding, or higher education,
              CashPro delivers <strong>customised loan products</strong> from India’s top banks and NBFCs.
              Our <strong>paperless, digital-first process</strong> ensures you get funds without friction.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <button className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center gap-2">
                <HandCoins size={20} /> Apply for Loan
              </button>
              <button className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105">
                Calculate EMI
              </button>
            </div>
          </div>
          <div className="flex justify-center animate-fade-in-up animation-delay-400">
            <img
              src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
              alt="Financial advisor discussing loan options"
              className="rounded-2xl shadow-2xl w-full max-w-md object-cover transition-transform duration-500 hover:scale-105"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-20 animate-fade-in-up animation-delay-600">
          {stats.map((stat, idx) => (
            <div
              key={idx}
              className="bg-white p-5 rounded-xl shadow-md text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100"
            >
              <stat.icon className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-800">{stat.value}</div>
              <div className="text-xs text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20 animate-fade-in-up">
          <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-2xl shadow-md border-l-8 border-green-600">
            <Target className="w-12 h-12 text-green-600 mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Our Mission</h3>
            <p className="text-gray-600 leading-relaxed">
              To democratize access to affordable credit for every Indian, whether salaried, self-employed,
              or entrepreneur. We aim to simplify loan processes through technology, transparency, and
              empathy – ensuring no dream remains unfunded.
            </p>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-white p-8 rounded-2xl shadow-md border-l-8 border-yellow-500">
            <Eye className="w-12 h-12 text-yellow-600 mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Our Vision</h3>
            <p className="text-gray-600 leading-relaxed">
              To become India’s most trusted loan facilitator, recognized for ethical practices, customer
              delight, and financial inclusion. We envision a future where securing a loan is as easy as
              online shopping.
            </p>
          </div>
        </div>
        <div className="mb-20 animate-fade-in-up">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
              Why India Chooses <span className="text-green-600">CashPro Financial Services</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto mt-3">
              We don't just give loans; we build financial confidence. Here's what makes us different.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <item.icon className="w-10 h-10 text-green-600 mb-4" />
                <h4 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h4>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-20 bg-white rounded-2xl p-8 shadow-md animate-fade-in-up">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800">
              Get Your Loan in <span className="text-green-600">3 Simple Steps</span>
            </h2>
            <p className="text-gray-500">No branch visits, no lengthy paperwork, no middlemen.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, idx) => (
              <div key={idx} className="text-center relative">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-8 h-8 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-green-600 mb-2">0{idx + 1}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm">{step.desc}</p>
                {idx < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/4 left-3/4 w-12 border-t-2 border-dashed border-green-300"></div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <p className="text-green-700 font-medium flex items-center justify-center gap-2">
              <BadgeCheck size={18} /> 100% digital process | No hidden fees | Free CIBIL check
            </p>
          </div>
        </div>
        <div className="mb-20 animate-fade-in-up">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800">
              What Our <span className="text-green-600">Clients Say</span>
            </h2>
            <p className="text-gray-500">Real stories, real impact – from people we’ve served.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={18}
                      className={i < t.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                    />
                  ))}
                </div>
                <p className="text-gray-600 italic mb-4">"{t.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center text-green-800 font-bold">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{t.name}</p>
                    <p className="text-xs text-gray-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-20 text-center animate-fade-in-up">
          <p className="text-gray-500 uppercase tracking-wider text-sm mb-6">
            Trusted by leading banks & financial institutions
          </p>
          <div className="flex flex-wrap justify-center gap-8 opacity-70 grayscale hover:grayscale-0 transition-all duration-300">
            {['HDFC Bank', 'ICICI Bank', 'SBI', 'Axis Bank', 'Bajaj Finserv', 'Tata Capital'].map(
              (bank) => (
                <span key={bank} className="text-gray-600 font-semibold text-lg">
                  {bank}
                </span>
              )
            )}
          </div>
        </div>
        <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-8 md:p-12 shadow-xl animate-fade-in-up text-white">
          <div className="flex flex-col md:flex-row gap-8 items-center justify-between">
            <div className="flex-1">
              <p className="text-xl md:text-2xl italic leading-relaxed">
                "The right financial support at the right time changes everything. Let CashPro be your
                growth catalyst."
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm font-bold text-lg">
                  CP
                </div>
                <div>
                  <p className="font-semibold">CashPro Financial Services</p>
                  <p className="text-sm text-white/80">Your Loan Partner Since 2014</p>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button className="bg-white text-green-700 hover:bg-gray-100 font-semibold px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-md flex items-center gap-2">
                <PhoneCall size={18} /> Talk to an Expert
              </button>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
        .animation-delay-600 {
          animation-delay: 0.6s;
        }
      `}</style>
    </section>
  );
};

export default AboutSection;